import React, { useState, useEffect } from 'react';
import { GoldTransaction, GoldSummary } from '../types/gold';
import { googleSheetsService } from '../services/googleSheets';
import { fetchGoldPrice, GoldPriceData } from '../services/goldPrice';

const GoldCalculator: React.FC = () => {
  const [transactions, setTransactions] = useState<GoldTransaction[]>([]);
  const [summary, setSummary] = useState<GoldSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [goldPrice, setGoldPrice] = useState<GoldPriceData | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    quantity: '',
    pricePerChi: '',
    notes: ''
  });

  // Load from Google Sheets on mount
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [sheetTransactions, priceData] = await Promise.all([
        googleSheetsService.getTransactions(),
        fetchGoldPrice(),
      ]);

      if (priceData) {
        setGoldPrice(priceData);
      }

      if (sheetTransactions.length > 0) {
        setTransactions(sheetTransactions);
        setSummary(calculateSummary(sheetTransactions, priceData));
        saveToLocalStorage(sheetTransactions);
      } else {
        // Fallback to localStorage if Google Sheets is empty
        const savedTransactions = localStorage.getItem('goldTransactions');
        if (savedTransactions) {
          const transactionsData: GoldTransaction[] = JSON.parse(savedTransactions);
          setTransactions(transactionsData);
          setSummary(calculateSummary(transactionsData, priceData));
        }
      }
    } catch (error) {
      console.error('Error loading from Google Sheets, falling back to localStorage:', error);
      const savedTransactions = localStorage.getItem('goldTransactions');
      if (savedTransactions) {
        const transactionsData: GoldTransaction[] = JSON.parse(savedTransactions);
        setTransactions(transactionsData);
        setSummary(calculateSummary(transactionsData));
      }
    } finally {
      setLoading(false);
    }
  };

  const saveToLocalStorage = (transactions: GoldTransaction[]) => {
    try {
      localStorage.setItem('goldTransactions', JSON.stringify(transactions));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const calculateSummary = (transactions: GoldTransaction[], priceData?: GoldPriceData | null): GoldSummary => {
    const totalQuantity = transactions.reduce((sum, t) => sum + t.quantity, 0);
    const totalCost = transactions.reduce((sum, t) => sum + t.totalCost, 0);
    const averagePricePerChi = totalQuantity > 0 ? totalCost / totalQuantity : 0;

    const summary: GoldSummary = {
      totalQuantity,
      totalCost,
      averagePricePerChi,
      transactionCount: transactions.length,
    };

    if (priceData && totalQuantity > 0) {
      summary.currentPricePerChi = priceData.buyPerChi;
      summary.currentValue = totalQuantity * priceData.buyPerChi;
      summary.profitLoss = summary.currentValue - totalCost;
      summary.profitLossPercent = (summary.profitLoss / totalCost) * 100;
    }

    return summary;
  };

  const handleAddTransaction = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.quantity || !formData.pricePerChi) {
      window.alert('Vui lòng nhập số chỉ và giá mỗi chỉ');
      return;
    }

    const quantity = parseFloat(formData.quantity);
    const pricePerChi = parseFloat(parseFormattedNumber(formData.pricePerChi));
    const totalCost = quantity * pricePerChi;

    const newTransaction: GoldTransaction = {
      id: `transaction-${Date.now()}`,
      date: formData.date,
      quantity,
      pricePerChi,
      totalCost,
      notes: formData.notes
    };

    const updatedTransactions = [...transactions, newTransaction];
    setTransactions(updatedTransactions);
    setSummary(calculateSummary(updatedTransactions, goldPrice));
    saveToLocalStorage(updatedTransactions);

    // Sync to Google Sheets
    googleSheetsService.addTransaction(newTransaction);

    // Reset form
    setFormData({
      date: new Date().toISOString().split('T')[0],
      quantity: '',
      pricePerChi: '',
      notes: ''
    });
    setShowAddForm(false);
  };

  const handleDeleteTransaction = async (id: string) => {
    if (window.confirm('Bạn có chắc muốn xóa giao dịch này?')) {
      const updatedTransactions = transactions.filter(t => t.id !== id);
      setTransactions(updatedTransactions);
      setSummary(calculateSummary(updatedTransactions, goldPrice));
      saveToLocalStorage(updatedTransactions);

      // Sync delete to Google Sheets
      googleSheetsService.deleteTransaction(id);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const formatNumberWithDots = (value: number): string => {
    return new Intl.NumberFormat('vi-VN').format(value);
  };

  const parseFormattedNumber = (value: string): string => {
    return value.replace(/\./g, '');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Đang tải...</div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto p-4 bg-white min-h-screen">
      <h1 className="text-2xl font-bold text-center mb-6 text-yellow-600">
        💰 Tích Vàng
      </h1>

      {/* Summary Card */}
      {summary && (
        <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg p-4 mb-6 border border-yellow-200">
          <h2 className="text-lg font-semibold mb-3 text-yellow-800">Tổng quan</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Số chỉ vàng:</span>
              <span className="font-bold">{summary.totalQuantity.toFixed(2)} chỉ</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Tổng chi phí:</span>
              <span className="font-bold">{formatCurrency(summary.totalCost)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Giá TB mỗi chỉ:</span>
              <span className="font-bold">{formatCurrency(summary.averagePricePerChi)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Số lần mua:</span>
              <span className="font-bold">{summary.transactionCount} lần</span>
            </div>
          </div>

          {/* Profit/Loss Section */}
          {summary.currentPricePerChi != null && summary.profitLoss != null && summary.profitLossPercent != null && (
            <div className="mt-4 pt-3 border-t border-yellow-300">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Giá mua vào BTSJC:</span>
                  <span className="font-bold">{formatCurrency(summary.currentPricePerChi)}/chỉ</span>
                </div>
                {goldPrice && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Giá bán ra BTSJC:</span>
                    <span className="font-bold">{formatCurrency(goldPrice.sellPerChi)}/chỉ</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Giá trị hiện tại:</span>
                  <span className="font-bold">{formatCurrency(summary.currentValue!)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Lời / Lỗ:</span>
                  <span className={`font-bold text-base ${summary.profitLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.profitLoss >= 0 ? '+' : ''}{formatCurrency(summary.profitLoss)}
                    {' '}
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${summary.profitLoss >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {summary.profitLoss >= 0 ? '+' : ''}{summary.profitLossPercent.toFixed(2)}%
                    </span>
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => {
            if (!showAddForm && goldPrice) {
              setFormData(prev => ({...prev, pricePerChi: formatNumberWithDots(goldPrice.sellPerChi)}));
            }
            setShowAddForm(!showAddForm);
          }}
          className="flex-1 bg-yellow-500 text-white py-3 rounded-lg font-medium hover:bg-yellow-600 transition-colors"
        >
          {showAddForm ? 'Hủy' : '+ Thêm giao dịch'}
        </button>

      </div>

      {/* Add Transaction Form */}
      {showAddForm && (
        <form onSubmit={handleAddTransaction} className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold mb-4">Thêm giao dịch mới</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ngày mua
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Số chỉ
              </label>
              <input
                type="number"
                step="0.1"
                placeholder="0.5"
                value={formData.quantity}
                onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Giá mỗi chỉ (VNĐ)
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="5.000.000"
                value={formData.pricePerChi}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^\d]/g, '');
                  if (raw === '') {
                    setFormData({...formData, pricePerChi: ''});
                  } else {
                    setFormData({...formData, pricePerChi: formatNumberWithDots(parseInt(raw, 10))});
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                required
              />
              {goldPrice && (
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({...prev, pricePerChi: formatNumberWithDots(goldPrice.sellPerChi)}))}
                  className="mt-1 text-xs text-yellow-700 bg-yellow-50 px-2 py-1 rounded hover:bg-yellow-100 transition-colors"
                >
                  Giá bán ra BTSJC: {formatCurrency(goldPrice.sellPerChi)}/chỉ
                </button>
              )}
            </div>

            {formData.quantity && formData.pricePerChi && (
              <div className="bg-yellow-50 p-3 rounded-md">
                <div className="text-sm text-gray-600">Tổng chi phí:</div>
                <div className="text-lg font-semibold text-yellow-700">
                  {formatCurrency(parseFloat(formData.quantity) * parseFloat(parseFormattedNumber(formData.pricePerChi)))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ghi chú (tùy chọn)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                rows={2}
                placeholder="Mua tại tiệm vàng ABC..."
              />
            </div>

            <button
              type="submit"
              className="w-full bg-yellow-500 text-white py-2 rounded-md font-medium hover:bg-yellow-600 transition-colors"
            >
              Thêm giao dịch
            </button>
          </div>
        </form>
      )}

      {/* Transactions List */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Lịch sử giao dịch</h2>
        {transactions.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            Chưa có giao dịch nào
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((transaction) => (
              <div key={transaction.id} className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="font-medium">{transaction.quantity} chỉ</div>
                    <div className="text-sm text-gray-500">{transaction.date}</div>
                    {transaction.notes && (
                      <div className="text-sm text-gray-600 mt-1">{transaction.notes}</div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{formatCurrency(transaction.totalCost)}</div>
                    <div className="text-sm text-gray-500">{formatCurrency(transaction.pricePerChi)}/chỉ</div>
                    <button
                      onClick={() => handleDeleteTransaction(transaction.id)}
                      className="text-red-500 text-sm mt-1 hover:text-red-700"
                    >
                      Xóa
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GoldCalculator;
